import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Upload, Trash2, Loader2, Check, AlertTriangle, SlidersHorizontal, Send } from 'lucide-react';
import { EMPTY_DELIVERY, deliveryComplete, deliveryPayload } from '@/lib/editorTime';
import { DeliveryQuestions } from './DeliveryQuestions';
import { MustHavesPanel, AttestationModal, useFirstDraftChecklist } from './MustHaves';

const ALLOWED_EXTS = ['.xmp', '.zip'];
const extOf = (name) => '.' + (name.split('.').pop() || '').toLowerCase();

// Editor-facing gate for photo drafts: every delivered photo must have its
// Lightroom develop settings attached before the draft can be submitted.
//
// Sidecars are stored PER PHOTO SET VERSION, which is what makes "each new
// version prompts you to update them" fall out for free — a fresh version starts
// at zero coverage, so this panel re-arms itself without any extra machinery.
export const SidecarPanel = ({ project, version, photoCount, onSubmitted }) => {
  const { user } = useAuth();
  const isEditor = user?.role === 'editor';
  const isAdmin = ['admin', 'staff'].includes(user?.role);
  const [state, setState] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // An editor's two answers about this delivery (DeliveryQuestions; the server refuses the submit without them).
  const [delivery, setDelivery] = useState(EMPTY_DELIVERY);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/photo-review/${project.id}/sidecar-status?version=${version}`);
      setState(r.data);
    } catch { /* panel simply stays hidden if this cannot be read */ }
  }, [project.id, version]);

  useEffect(() => { load(); }, [load]);
  // The first-draft checklist (photo carries the notes confirmation alone): Submit Draft waits for it.
  const checklist = useFirstDraftChecklist(project.id, isEditor);
  const [attesting, setAttesting] = useState(false);

  if (!state || (!isEditor && !isAdmin)) return null;
  // Nothing to gate: culling jobs, grandfathered projects, and video never see this.
  if (!state.required) return null;
  if (!photoCount) return null;

  const complete = state.complete;
  const alreadySubmitted = (project.editor_status_id || 0) >= 2;

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList);
    const bad = files.filter((f) => !ALLOWED_EXTS.includes(extOf(f.name)));
    if (bad.length) {
      toast.error(`Only .xmp files or a .zip of them — rejected: ${bad.map((f) => f.name).slice(0, 3).join(', ')}`);
      return;
    }
    setUploading(true);
    const uploaded = [];
    try {
      for (const f of files) {
        const presign = await api.post('/storage/presign-upload', {
          filename: f.name, scope: 'project_deliverable', project_id: project.id,
          version: String(version), content_type: f.type || 'application/octet-stream',
        });
        if (presign.data.upload_url && presign.data.upload_url !== 'LOCAL') {
          const putRes = await fetch(presign.data.upload_url, {
            method: 'PUT', body: f, headers: { 'Content-Type': f.type || 'application/octet-stream' } });
          if (!putRes.ok) throw new Error(`Storage upload failed (${putRes.status})`);
        } else {
          const form = new FormData(); form.append('file', f);
          await api.post(`/storage/upload/${presign.data.file_id}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        await api.post(`/storage/confirm-upload/${presign.data.file_id}`);
        uploaded.push({ file_id: presign.data.file_id, filename: f.name, key: presign.data.key });
      }
      const r = await api.post(`/photo-review/${project.id}/sidecars`, { version, files: uploaded });
      setState(r.data);
      toast.success(r.data.complete
        ? `All ${r.data.total} photos have edit settings — ready to submit`
        : `${r.data.covered} of ${r.data.total} photos covered`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not attach the sidecars');
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleRemove = async (fid) => {
    try {
      const r = await api.delete(`/photo-review/${project.id}/sidecars/${fid}?version=${version}`);
      setState(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove'); }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/photo-review/${project.id}/submit-draft`,
        { version, ...(isEditor ? { delivery_answers: deliveryPayload(delivery) } : {}) });
      toast.success(r.data.override
        ? 'Draft submitted with missing edit settings (admin override)'
        : 'Draft submitted');
      setDelivery(EMPTY_DELIVERY);
      onSubmitted && onSubmitted();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Could not submit the draft', { duration: 12000 });
    }
    setSubmitting(false);
  };

  return (
    <div className="pt-3 border-t border-white/[0.04] space-y-2" data-testid="sidecar-panel">
      <div className="flex items-center justify-between">
        <p className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">
          Edit Settings (XMP)
        </p>
        <span data-testid="sidecar-coverage"
          className={`font-sans text-[9px] font-semibold ${complete ? 'text-oxidized-teal' : 'text-amber-400'}`}>
          {complete
            ? <><Check className="w-3 h-3 inline mr-0.5" />{state.total} of {state.total} covered</>
            : <><AlertTriangle className="w-3 h-3 inline mr-0.5" />{state.covered} of {state.total} covered</>}
        </span>
      </div>

      {!complete && (
        <p className="font-sans text-[9px] text-slate-400 leading-relaxed" data-testid="sidecar-missing-hint">
          {state.missing_count} photo{state.missing_count !== 1 ? 's' : ''} still need their develop settings.
          In Lightroom select all, then <span className="text-zinc-300">Metadata → Save Metadata to File</span>, and
          upload the .xmp files (a .zip is fine).
          {state.missing_sample?.length > 0 && (
            <span className="block mt-1 text-slate-500">
              Missing: {state.missing_sample.slice(0, 6).join(', ')}
              {state.missing_count > 6 ? ` +${state.missing_count - 6} more` : ''}
            </span>
          )}
        </p>
      )}

      {state.files?.length > 0 && (
        <ul className="space-y-1" data-testid="sidecar-file-list">
          {state.files.map((f) => (
            <li key={f.file_id} className="flex items-center justify-between gap-2 text-[9px] font-mono text-slate-400">
              <span className="truncate min-w-0">
                <SlidersHorizontal className="w-3 h-3 inline mr-1 opacity-60" />
                {f.filename}{f.sidecar_count ? ` — ${f.sidecar_count} sidecars` : ''}
              </span>
              <button onClick={() => handleRemove(f.file_id)} data-testid={`sidecar-remove-${f.file_id}`}
                className="tap-auto shrink-0 text-slate-500 hover:text-ug-status-red" title="Remove">
                <Trash2 className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {isEditor && !alreadySubmitted && checklist.needed && (
        <MustHavesPanel projectId={project.id} gate={checklist.gate} onChanged={checklist.load} onConfirm={() => setAttesting(true)} />
      )}
      {attesting && checklist.gate && (
        <AttestationModal projectId={project.id} items={checklist.gate.items} onClose={() => setAttesting(false)}
          onComplete={(att) => { checklist.confirmed(att); setAttesting(false); }} />
      )}
      {isEditor && !alreadySubmitted && <DeliveryQuestions value={delivery} onChange={setDelivery} />}

      <div className="flex items-center gap-2">
        <input ref={inputRef} type="file" multiple accept=".xmp,.zip" className="hidden"
          data-testid="sidecar-file-input"
          onChange={(e) => e.target.files?.length && handleFiles(e.target.files)} />
        <Button size="sm" variant="outline" disabled={uploading} onClick={() => inputRef.current?.click()}
          data-testid="sidecar-upload-btn"
          className="h-7 font-sans text-[9px] font-semibold border-ug-border text-slate-400 hover:text-zinc-100">
          {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
          {uploading ? 'Uploading...' : 'Attach XMP'}
        </Button>

        {!alreadySubmitted && (
          <Button size="sm" onClick={handleSubmit}
            disabled={submitting || (!complete && !isAdmin) || (isEditor && (checklist.needed || checklist.pending || !deliveryComplete(delivery)))}
            data-testid="sidecar-submit-draft-btn"
            title={isEditor && checklist.needed ? 'Confirm the checklist above first'
              : isEditor && !deliveryComplete(delivery) ? 'Answer the two questions about this delivery first'
              : complete ? 'Submit this draft for review'
              : isAdmin ? 'Submit without complete edit settings (recorded as an override)'
                : 'Attach the missing XMP sidecars before submitting'}
            className="h-7 font-sans text-[9px] font-semibold bg-gold-leaf text-ug-obsidian hover:bg-gold-leaf/90">
            {submitting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
            {complete || !isAdmin ? 'Submit Draft' : 'Submit Anyway'}
          </Button>
        )}
      </div>
    </div>
  );
};
